/*  eslint no-param-reassign: ["error", { "props": false }]*/

import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { required, numericRange } from '@mhub/web-validations';
import {
  Divider,
  Form,
  Grid,
  Dropdown,
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import config from '../../../../config';
import { fetchM } from '../../../actions/fetchM';
import { flashErrorMessage } from '../../../actions/flash';
import AppForm from '../../../components/AppForm';
import TextArea from '../../../components/AppForm/TextArea';
import SpecificationTable from '../../../components/SpecificationTable';
import { fullToAmount } from '../../../utils/amount';
import { PROPERTY_CATEGORY_HIGH_RISE } from '../../../constants/propertyCategories';
import { getItem } from '../../../utils/storage';
import { namespaces, TRANSLATION_FILES } from '../../../constants/translation';
import { getLandAreaModelLabel, getUnitFormAreaLabel } from '../../../utils/areaLabel';
import { canEditVietnamUnitPricing, isVietnamCompany, vnUnitTextValues } from '../../../utils/vietnam';

class UnitForm extends Component {
  static propTypes = {
    id: PropTypes.string,
    project: PropTypes.shape({
      name: PropTypes.string,
      propertyCategory: PropTypes.shape({
        category: PropTypes.string,
      }),
    }),
    onSubmit: PropTypes.func.isRequired,
    onChange: PropTypes.func,
    unit: PropTypes.shape({
      reference: PropTypes.string,
    }),
    location: PropTypes.shape({
      query: PropTypes.object,
    }),
    t: PropTypes.func,
  }
  static contextTypes = {
    store: PropTypes.object,
  }
  static defaultValues = {
    isBumi: false,
    projectID: '',
    projectUnitLayoutID: '',
    reference: '',
    statusID: 'new',
    position: 1,
    spaValue: 0,
    spaDecimal: 0,
    spaFull: 0,
    spaTimes: 100,
    availableUnits: 1,
    size: 0,
    address: '',
    streetNumberOrLevel: '',
    streetOrFacing: '',
    zoneOrBlock: '',
    specifications: [],
    colSpan: 1,
    companyBranchAllocationIDs: [],
    // CPI-1219: Vietnam only on the screen, but harmless defaults everywhere -- an empty string
    // keeps the inputs controlled, and vnUnitTextValues decides whether the keys are ever sent.
    unitLayoutNew: '',
    view: '',
  }
  constructor(props) {
    super(props);
    const unit = Object.assign({}, UnitForm.defaultValues, props.unit || {});
    if (props.location && props.location.query.unitLayoutID) {
      unit.projectUnitLayoutID = props.location.query.unitLayoutID;
    }
    let globalSettings = {};
    const storeGloablSettings = getItem(`${config.get('appName')}.globalSettings`);
    if (storeGloablSettings) {
      try {
        globalSettings = JSON.parse(storeGloablSettings).data || [];
      } catch (e) {
        Bugsnag.notifyException(e);
      }
    }
    this.state = {
      unit,
      unitLayouts: [],
      errMessages: [],
      globalSettings,
    };
    this.validation = {
      reference: createRef(),
      statusID: createRef(),
      projectUnitLayoutID: createRef(),
      size: createRef(),
      landArea: createRef(),
      unitLayoutNew: createRef(),
      view: createRef(),
      vatPercentage: createRef(),
      landValue: createRef(),
      spaValue: createRef(),
      position: createRef(),
      colSpan: createRef(),
      zoneOrBlock: createRef(),
      streetNumberOrLevel: createRef(),
      streetOrFacing: createRef(),
      virtualTourURL: createRef(),
      companyBranchAllocationIDs: createRef(),
    };
  }
  componentDidMount() {
    this.fetchProjectUnitLayouts();
    this.fetchCompanyBranches();
  }
  componentWillReceiveProps(nextProps) {
    let unit = Object.assign({}, UnitForm.defaultValues, this.state.unit);
    if (JSON.stringify(this.props.unit) !== JSON.stringify(nextProps.unit)) {
      unit = Object.assign(unit, nextProps.unit);
    }
    this.setState({ unit });
  }
  getAuth() {
    const { store } = this.context;
    return (store && store.getState && store.getState().auth) || null;
  }
  // The three gates this form reads, all false outside Vietnam.
  getVietnamGates() {
    const auth = this.getAuth();
    const isVietnam = isVietnamCompany(auth);
    return {
      isVietnam,
      countryCode: (auth && auth.company && auth.company.countryCode) || '',
      canEditVnPricing: canEditVietnamUnitPricing(auth),
    };
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  async fetchProjectUnitLayouts() {
    const { store } = this.context;
    let res;
    try {
      res = await store.dispatch(fetchM(`/projects/${this.props.id}/unit_layouts/`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: 'An error occurred, please refresh page' }));
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const unitLayouts = await res.json();
      // handle token.. etc
      const { unit } = this.state;
      if (unit.projectUnitLayoutID.length > 0 && unit.size < 1) {
        unit.size = unitLayouts.filter(ul => ul.id === unit.projectUnitLayoutID)[0].size;
      }
      await this.setComponentState({
        unit,
        unitLayouts: unitLayouts ? unitLayouts.map((ul) => {
          const unitLayout = { text: ul.name, name: ul.id, value: ul.id, size: ul.size };
          return unitLayout;
        }) : [],
      });
    }
  }
  async fetchCompanyBranches() {
    const { store } = this.context;
    let res;
    try {
      res = await store.dispatch(fetchM(`${config.get('bookApiURL')}/company_branches/?isExternal=true`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: 'An error occurred, please refresh page' }));
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const json = await res.json();
      if (json) {
        const data = json.map((obj) => {
          const newResults = {};
          newResults.text = obj.name;
          newResults.value = obj.id;
          return newResults;
        });
        await this.setComponentState({
          companyBranches: data,
        });
      }
    }
  }
  submit(url, opts) {
    return this.form.submit(url, opts);
  }
  handleSubmit = (event) => {
    event.preventDefault();
    const { unit } = this.state;
    if (unit.specifications) {
      unit.specifications = unit.specifications.filter(data =>
        data.name.length > 0 && data.value.length > 0).map((data, idx) => ({
          ...data,
          position: idx + 1,
        }));
    }
    AppForm.validate(this.validation, unit)
      .then((validations) => {
        const { isVietnam, canEditVnPricing } = this.getVietnamGates();
        let data = Object.assign(unit, {
          spaValue: parseFloat(unit.spaValue),
          size: parseFloat(unit.size),
          position: parseInt(unit.position, 10),
          colSpan: parseInt(unit.colSpan, 10),
          // CPI-1230 (D-D6): a key is only added when the input actually rendered, so saving the
          // rest of the form cannot blank a stored VAT % or Land Value.
          ...(isVietnam ? { landArea: parseFloat(unit.landArea) || 0 } : {}),
          // CPI-1219: the unit's own Unit Layout and View. Free text, so nothing is parsed.
          ...vnUnitTextValues(unit, isVietnam),
          ...(canEditVnPricing ? {
            vatPercentage: parseFloat(unit.vatPercentage) || 0,
            landValue: parseFloat(unit.landValue) || 0,
          } : {}),
        });
        data = fullToAmount(data, unit.spaValue);
        this.props.onSubmit(validations, data);
      });
  }
  handleCheckChanges() {
    if (this.props.onChange) {
      this.props.onChange(true);
    }
  }
  handleInputChange = (event, { name, value }) => {
    const { unit, unitLayouts } = this.state;
    unit[name] = value;
    if (name === 'projectUnitLayoutID') {
      if (unitLayouts.length > 0 && unit.size === 0) {
        const selectedLayout = unitLayouts.find(ul => ul.id === unit.projectUnitLayoutID);
        if (selectedLayout && selectedLayout.size > 0) {
          unit.size = selectedLayout.size;
        }
      }
    }
    this.setState({ unit }, () => {
      this.handleCheckChanges();
    });
  }
  handleCheckBoxChange = (event, { name }) => {
    const { unit } = this.state;
    unit[name] = !unit[name];
    this.setState({ unit }, () => {
      this.handleCheckChanges();
    });
  }
  handleSpecificationsChange = (data) => {
    const { unit } = this.state;
    unit.specifications = data;
    this.setState({ unit }, () => {
      this.handleCheckChanges();
    });
  }
  checkStatusDropDown = () => {
    switch (this.state.unit.statusID) {
      case 'new':
      case 'developer_booked':
      case 'delist':
        return true;
      default:
        return false;
    }
  }
  render() {
    const { t } = this.props;
    const { companyBranches, globalSettings } = this.state;
    const { isVietnam, countryCode, canEditVnPricing } = this.getVietnamGates();
    const statuses = [
      { name: 'new', value: 'new', text: t('Available', { ns: TRANSLATION_FILES.constants }) },
      // { name: 'reserved', value: 'reserved', text: 'Reserved' },
      // { name: 'booked', value: 'booked', text: 'Booked' },
      { name: 'developer_booked', value: 'developer_booked', text: t('Hold', { ns: TRANSLATION_FILES.constants }) },
      { name: 'delist', value: 'delist', text: t('Delist', { ns: TRANSLATION_FILES.constants }) },
    ];
    return (
      <AppForm
        onSubmit={this.handleSubmit}
        error={this.state.errMessages.length > 0}
        ref={(ref) => { this.form = ref; }}
      >
        <Grid columns="equal" stackable doubling>
          <Grid.Row>
            <Grid.Column>
              <Form.Group widths="equal">
                <AppForm.Input
                  label={t('Project', { ns: TRANSLATION_FILES.screens })}
                  name="projectID"
                  disabled
                  value={this.props.project ? this.props.project.name : t('Project', { ns: TRANSLATION_FILES.screens })}
                />
                <AppForm.Validate
                  validation={[required]}
                  ref={this.validation.projectUnitLayoutID}
                  onChange={this.handleInputChange}
                >
                  <Form.Select
                    label={t('Layout Type', { ns: TRANSLATION_FILES.screens })}
                    name="projectUnitLayoutID"
                    search
                    fluid
                    text={this.state.unit.projectUnitLayoutID ? null : t('Select layout type', { ns: TRANSLATION_FILES.screens })}
                    value={this.state.unit.projectUnitLayoutID}
                    options={this.state.unitLayouts}
                    disabled={this.state.unitLayouts.length === 0}
                  />
                </AppForm.Validate>
              </Form.Group>
              <br />
              <Divider horizontal>{t('Info', { ns: TRANSLATION_FILES.screens })}</Divider>
              <Form.Group widths="equal">
                <AppForm.Input
                  label={t('Unit Number', { ns: TRANSLATION_FILES.screens })}
                  name="reference"
                  value={this.state.unit.reference}
                  onChange={this.handleInputChange}
                  ref={this.validation.reference}
                  validation={[required]}
                />
                {
                  this.checkStatusDropDown() ?
                    <AppForm.Validate
                      validation={[required]}
                      ref={this.validation.statusID}
                      onChange={this.handleInputChange}
                    >
                      <Form.Select
                        label={t('Status', { ns: TRANSLATION_FILES.screens })}
                        name="statusID"
                        search
                        fluid
                        value={this.state.unit.statusID}
                        options={statuses}
                      />
                    </AppForm.Validate>
                  :
                    <AppForm.Input
                      label={t('Status', { ns: TRANSLATION_FILES.screens })}
                      name="statusID"
                      disabled
                      value={t(this.state.unit.status.name, { ns: TRANSLATION_FILES.constants })}
                    />
                }
              </Form.Group>
              <Form.Group widths="equal">
                {/* CPI-1230 (D-D6): the same `size` column the unit screen shows. Vietnam calls it
                    NSA - Net Saleable Area; Malaysia and Indonesia keep the plain "Size" key. */}
                <AppForm.Input
                  label={getUnitFormAreaLabel(t, countryCode)}
                  name="size"
                  type="number"
                  step="0.0001"
                  value={this.state.unit.size}
                  onChange={this.handleInputChange}
                  ref={this.validation.size}
                  validation={[required, {
                    validate: numericRange,
                    option: {
                      min: 1,
                    },
                  }]}
                />
                <AppForm.Input
                  label={
                    globalSettings && globalSettings.sellingPrice
                      ? globalSettings.sellingPrice
                      : t('Selling Price', { ns: TRANSLATION_FILES.screens })
                  }
                  name="spaValue"
                  type="number"
                  step=".01"
                  value={this.state.unit.spaValue}
                  onChange={this.handleInputChange}
                  ref={this.validation.spaValue}
                  validation={[required, {
                    validate: numericRange,
                    option: {
                      min: 1,
                    },
                  }]}
                />
              </Form.Group>
              {/* CPI-1230 (D-D6): Vietnam's second area. It is the unit's `landArea` column - the
                  one Unit/Info.jsx labels NFA - Net Floor Area - and it is optional, because
                  Malaysia has never asked for it on this form and a Vietnamese import may not
                  carry it. Nothing renders outside Vietnam, so the Malaysian form is unchanged. */}
              {isVietnam &&
                <Form.Group widths="equal">
                  <AppForm.Input
                    label={getLandAreaModelLabel(t, countryCode)}
                    name="landArea"
                    type="number"
                    step="0.0001"
                    value={this.state.unit.landArea || 0}
                    onChange={this.handleInputChange}
                    ref={this.validation.landArea}
                    validation={[{
                      validate: numericRange,
                      option: {
                        min: 0,
                      },
                    }]}
                  />
                </Form.Group>
              }
              {/* CPI-1219: the unit's OWN layout description and view, which the product owner
                  separated from the linked layout above and from the layout's own view on
                  6 September. Free text with no list to match, and no role gate - every role that
                  can reach this form may type them. Nothing renders outside Vietnam. */}
              {isVietnam &&
                <Form.Group widths="equal">
                  <AppForm.Input
                    label={t('Unit Layout', { ns: TRANSLATION_FILES.screens })}
                    name="unitLayoutNew"
                    placeholder="2PN-2WC"
                    value={this.state.unit.unitLayoutNew || ''}
                    onChange={this.handleInputChange}
                    ref={this.validation.unitLayoutNew}
                  />
                  <AppForm.Input
                    label={t('View', { ns: TRANSLATION_FILES.screens })}
                    name="view"
                    placeholder="SE"
                    value={this.state.unit.view || ''}
                    onChange={this.handleInputChange}
                    ref={this.validation.view}
                  />
                </Form.Group>
              }
              {/* CPI-1230 (D4): VAT % and Land Value are stored, never calculated - there is no VAT
                  amount and no before/after-tax price in this release. They are internal pricing,
                  so an external agent never sees them and only HQ / Sales Admin holding
                  allowEditUnitPrice may type them, the same rule Unit/Info.jsx applies. */}
              {canEditVnPricing &&
                <Form.Group widths="equal">
                  <AppForm.Input
                    label={t('VAT %', { ns: TRANSLATION_FILES.screens })}
                    name="vatPercentage"
                    type="number"
                    step="0.001"
                    value={this.state.unit.vatPercentage || 0}
                    onChange={this.handleInputChange}
                    ref={this.validation.vatPercentage}
                    validation={[{
                      validate: numericRange,
                      option: {
                        min: 0,
                        max: 100,
                      },
                    }]}
                  />
                  <AppForm.Input
                    label={t('Land Value', { ns: TRANSLATION_FILES.screens })}
                    name="landValue"
                    type="number"
                    step="0.01"
                    value={this.state.unit.landValue || 0}
                    onChange={this.handleInputChange}
                    ref={this.validation.landValue}
                    validation={[{
                      validate: numericRange,
                      option: {
                        min: 0,
                      },
                    }]}
                  />
                </Form.Group>
              }
              {/* <Form.Group>
                <Form.Checkbox
                  label="Bumiputera Unit"
                  name="isBumi"
                  checked={this.state.unit.isBumi}
                  onChange={this.handleCheckBoxChange}
                />
              </Form.Group> */}
              <SpecificationTable
                type="units"
                editable
                isEditing
                hideButton
                data={this.state.unit.specifications}
                onChange={this.handleSpecificationsChange}
              />
              <br />
              <Divider horizontal>{t('Location', { ns: TRANSLATION_FILES.screens })}</Divider>
              <Form.Group widths="equal">
                <AppForm.Input
                  label={this.props.project &&
                    this.props.project.propertyCategory.category === PROPERTY_CATEGORY_HIGH_RISE ?
                    t('Block', { ns: TRANSLATION_FILES.screens }) : t('Zone', { ns: TRANSLATION_FILES.screens })
                  }
                  name="zoneOrBlock"
                  value={this.state.unit.zoneOrBlock}
                  onChange={this.handleInputChange}
                  ref={this.validation.zoneOrBlock}
                />
                <AppForm.Input
                  label={this.props.project &&
                    this.props.project.propertyCategory.category === PROPERTY_CATEGORY_HIGH_RISE ?
                    t('Level', { ns: TRANSLATION_FILES.screens }) : t('Street Number', { ns: TRANSLATION_FILES.screens })
                  }
                  name="streetNumberOrLevel"
                  value={this.state.unit.streetNumberOrLevel}
                  onChange={this.handleInputChange}
                  ref={this.validation.streetNumberOrLevel}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <AppForm.Input
                  label={this.props.project &&
                    this.props.project.propertyCategory.category === PROPERTY_CATEGORY_HIGH_RISE ?
                    t('Facing', { ns: TRANSLATION_FILES.screens }) : t('Street', { ns: TRANSLATION_FILES.screens })
                  }
                  name="streetOrFacing"
                  value={this.state.unit.streetOrFacing}
                  onChange={this.handleInputChange}
                  ref={this.validation.streetOrFacing}
                />
                <AppForm.Input
                  label={t('Position', { ns: TRANSLATION_FILES.screens })}
                  name="position"
                  type="number"
                  value={this.state.unit.position}
                  onChange={this.handleInputChange}
                  ref={this.validation.position}
                  validation={[{
                    validate: numericRange,
                    option: {
                      min: 1,
                    },
                  }]}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <AppForm.Input
                  label={t('Col Span in sales chart', { ns: TRANSLATION_FILES.screens })}
                  name="colSpan"
                  type="number"
                  value={this.state.unit.colSpan}
                  onChange={this.handleInputChange}
                  ref={this.validation.colSpan}
                  validation={[{
                    validate: numericRange,
                    option: {
                      min: 1,
                    },
                  }]}
                />
              </Form.Group>
              <TextArea
                ref={this.validation.address}
                onChange={this.handleInputChange}
                label={t('Address', { ns: TRANSLATION_FILES.screens })}
                name="address"
                value={this.state.unit.address}
              />
              <br />
              <Divider horizontal>{t('Virtual Tour', { ns: TRANSLATION_FILES.screens })}</Divider>
              <AppForm.Input
                label={t('URL', { ns: TRANSLATION_FILES.screens })}
                name="virtualTourURL"
                value={this.state.unit.virtualTourURL}
                onChange={this.handleInputChange}
                ref={this.validation.virtualTourURL}
              />
              <Divider horizontal style={{ margin: '3em 0 2em 0' }}>{t('Agency', { ns: TRANSLATION_FILES.screens })}</Divider>
              <Form.Group widths="equal">
                <Dropdown
                  name="companyBranchAllocationIDs"
                  onChange={this.handleInputChange}
                  placeholder={t('Select Agency', { ns: TRANSLATION_FILES.screens })}
                  label={t('Select Agency', { ns: TRANSLATION_FILES.screens })}
                  fluid
                  multiple
                  selection
                  search
                  options={companyBranches || []}
                  value={this.state.unit.companyBranchAllocationIDs ? this.state.unit.companyBranchAllocationIDs : []}
                  ref={this.validation.companyBranchAllocationIDs}
                />
              </Form.Group>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </AppForm>
    );
  }
}

export default withTranslation(namespaces, { withRef: true })(UnitForm);



// WEBPACK FOOTER //
// ./src/containers/Project/Units/Form.jsx